import type { AiPromptUseCaseId } from '../../admin/types/AiPromptConfig';
import { DEFAULT_ASSIGNED_USE_CASE_IDS } from '../../tools/constants/assignableUseCases';
import type { AiToolPluginCatalogItem } from '../types/ToolPlugin';

export const RESERVED_KERNEL_PLUGIN_NAMES = [
  'mcpTools',
  'apiTools',
  'orchestratorTools',
  'dynamicKnowledge',
];

export const KERNEL_PLUGIN_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

export type ToolPluginFormState = {
  pluginId: string;
  kernelPluginName: string;
  displayName: string;
  description: string;
  isPublished: boolean;
  memberFunctionIds: string[];
  assignedUseCaseIds: AiPromptUseCaseId[];
};

export const emptyToolPluginForm: ToolPluginFormState = {
  pluginId: '',
  kernelPluginName: '',
  displayName: '',
  description: '',
  isPublished: false,
  memberFunctionIds: [],
  assignedUseCaseIds: [...DEFAULT_ASSIGNED_USE_CASE_IDS],
};

export function suggestKernelPluginName(displayName: string): string {
  const words = displayName
    .trim()
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean);

  if (words.length === 0) {
    return '';
  }

  const candidate = words
    .map((word, index) => (index === 0
      ? word.charAt(0).toLowerCase() + word.slice(1)
      : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('');

  const prefixed = /^[A-Za-z]/.test(candidate) ? candidate : `p${candidate}`;
  return prefixed.slice(0, 64);
}

export function isReservedKernelPluginName(kernelPluginName: string): boolean {
  const normalized = kernelPluginName.trim().toLowerCase();
  return RESERVED_KERNEL_PLUGIN_NAMES.some((reserved) => reserved.toLowerCase() === normalized);
}

export type ToolPluginFormIssue =
  | 'displayNameRequired'
  | 'kernelPluginNameRequired'
  | 'kernelPluginNameInvalid'
  | 'kernelPluginNameReserved'
  | 'kernelPluginNameTaken'
  | 'publishedWithoutMembers'
  | 'duplicateFunctionName';

export function validateToolPluginForm(
  form: ToolPluginFormState,
  options: {
    existingKernelPluginNames: { pluginId: string; kernelPluginName: string }[];
    catalog: AiToolPluginCatalogItem[];
  },
): ToolPluginFormIssue | null {
  const displayName = form.displayName.trim();
  const kernelPluginName = form.kernelPluginName.trim();

  if (!displayName) {
    return 'displayNameRequired';
  }

  if (!kernelPluginName) {
    return 'kernelPluginNameRequired';
  }

  if (!KERNEL_PLUGIN_NAME_PATTERN.test(kernelPluginName)) {
    return 'kernelPluginNameInvalid';
  }

  if (isReservedKernelPluginName(kernelPluginName)) {
    return 'kernelPluginNameReserved';
  }

  const taken = options.existingKernelPluginNames.some((item) => (
    item.pluginId !== form.pluginId
    && item.kernelPluginName.toLowerCase() === kernelPluginName.toLowerCase()
  ));
  if (taken) {
    return 'kernelPluginNameTaken';
  }

  if (form.isPublished && form.memberFunctionIds.length === 0) {
    return 'publishedWithoutMembers';
  }

  const seenFunctionNames = new Set<string>();
  const members = options.catalog.filter((item) => form.memberFunctionIds.includes(item.functionId));
  for (const member of members) {
    const name = member.kernelFunctionName.toLowerCase();
    if (seenFunctionNames.has(name)) {
      return 'duplicateFunctionName';
    }
    seenFunctionNames.add(name);
  }

  return null;
}
